import { DsButton, DsHeading, DsIconButton, DsModal } from '@frontends/ds';
import { cn } from '@frontends/util-cn';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import {
  type BaseSyntheticEvent,
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { showToast } from '@/components/Toast.tsx';
import useAuth from '@/hooks/useAuth.tsx';
import useAxiosInterceptors from '@/hooks/useAxiosInterceptors.tsx';
import { useEntityConfig } from '@/hooks/useEntityConfig';
import { AddressData, ClientSummary, EnhancedClientSummaryResponse } from '@/types/Auth.ts';
import { IGenericError } from '@/types/Error.ts';
import {
  onboardingSchemaAddressInformation,
  OnboardingSchemaAddressInformationType,
  onboardingSchemaBasicInformation,
  OnboardingSchemaBasicInformationType,
  OnboardingSteps,
} from '@/types/Onboarding.ts';
import { parseBirthday } from '@/utils/birthday.ts';
import { OnboardingAddressInfoUpdate } from './OnboardingAddressInfoUpdate.tsx';
import { OnboardingBasicInfoUpdate } from './OnboardingBasicInfoUpdate.tsx';
import { OnboardingVerify } from './OnboardingVerify.tsx';

const debugOnboardingSteps = [
  OnboardingSteps.MISSING_VERIFICATION,
  OnboardingSteps.MISSING_BASIC_INFORMATION,
  OnboardingSteps.MISSING_ADDRESS_INFORMATION,
];

const OnboardingModal = memo(function OnboardingModal({
  clientQuery,
  onboardingModalOpen,
  setOnboardingModalOpen,
}: {
  clientQuery: UseQueryResult<EnhancedClientSummaryResponse, Error>;
  onboardingModalOpen: boolean;
  setOnboardingModalOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const axiosPrivate = useAxiosInterceptors();
  const { client } = useAuth();
  const config = useEntityConfig();
  const popupWasOpen = useRef(false);

  const [availableSteps, setAvailableSteps] = useState<OnboardingSteps[]>([]);
  const [step, setStep] = useState<OnboardingSteps | undefined>(undefined);

  const getSortedOnboardingSteps = (onboardingSteps: OnboardingSteps[]) => {
    return [...(onboardingSteps ?? [])].sort((a, b) => a - b);
  };

  useEffect(() => {
    if (!config.IS_SHOW_ONBOARDING_DEBUG_MODE) {
      return;
    }

    const sorted = getSortedOnboardingSteps(debugOnboardingSteps);
    setAvailableSteps(sorted);
    setStep(sorted[0]);
  }, [config.IS_SHOW_ONBOARDING_DEBUG_MODE]);

  useEffect(() => {
    if (config.IS_SHOW_ONBOARDING_DEBUG_MODE) {
      return;
    }

    const sorted = getSortedOnboardingSteps(client?.client_details?.onboarding_steps ?? []);
    setAvailableSteps(sorted);
    setStep(sorted[0]);
  }, [client?.client_details?.onboarding_steps, config.IS_SHOW_ONBOARDING_DEBUG_MODE]);

  const onboardingBasicInfoForm = useForm<OnboardingSchemaBasicInformationType>({
    criteriaMode: 'all',
    resolver: zodResolver(onboardingSchemaBasicInformation(t)),
    defaultValues: {
      first_name: client?.client_details?.first_name,
      last_name: client?.client_details?.last_name,
      birthday: parseBirthday(client?.client_details?.birthday),
    },
  });

  const onboardingAddressInfoForm = useForm<OnboardingSchemaAddressInformationType>({
    criteriaMode: 'all',
    resolver: zodResolver(onboardingSchemaAddressInformation(t)),
    defaultValues: {
      state: client?.addresses?.[0]?.state,
      city: client?.addresses?.[0]?.city,
      postcode: client?.addresses?.[0]?.postcode,
      address: client?.addresses?.[0]?.address,
    },
  });

  const onboardingBasicInfoApi = useCallback(
    async (data: OnboardingSchemaBasicInformationType) => {
      const response = await axiosPrivate.patch<ClientSummary>('/clients/current/update-details', {
        ...data,
        birthday: format(data.birthday, 'yyyy-MM-dd'),
      });

      return response.data;
    },
    [axiosPrivate],
  );

  const onboardingAddressInfoApi = useCallback(
    async (data: OnboardingSchemaAddressInformationType) => {
      const response = await axiosPrivate.patch<AddressData>('/clients/current/update-address', data);

      return response.data;
    },
    [axiosPrivate],
  );

  const onBoardingBasicInfoMutation = useMutation({
    mutationKey: ['onboarding-basic-info'],
    mutationFn: onboardingBasicInfoApi,
    onSuccess: async (clientDetails) => {
      await fetchCurrentClientAndSetOnboardingSteps();
      onboardingBasicInfoForm.reset({
        first_name: clientDetails?.first_name,
        last_name: clientDetails?.last_name,
        birthday: parseBirthday(clientDetails?.birthday),
      });
      showToast({
        type: 'success',
        title: t('basic_information_updated_title'),
        description: t('basic_information_updated_desc'),
      });
    },
    onError: (e: AxiosError<IGenericError>) => {
      onboardingBasicInfoForm.reset();
      showToast({
        type: 'error',
        title: t('basic_information_updated_error_title'),
        description: t(e?.response?.data.context?.translationKey ?? '', e?.response?.data?.detail ?? e.message),
      });
    },
  });

  const fetchCurrentClientAndSetOnboardingSteps = useCallback(async () => {
    const response = await axiosPrivate.get<EnhancedClientSummaryResponse>('/clients/current');

    if (config.IS_SHOW_ONBOARDING_DEBUG_MODE) {
      return response.data;
    }

    const { client_details } = response.data;
    const sorted = getSortedOnboardingSteps(client_details?.onboarding_steps ?? []);
    setAvailableSteps(sorted);
    setStep(sorted[sorted.length - 1]);

    return response.data;
  }, [axiosPrivate, config.IS_SHOW_ONBOARDING_DEBUG_MODE]);

  const onBoardingAddressInfoMutation = useMutation({
    mutationKey: ['onboarding-address-info'],
    mutationFn: onboardingAddressInfoApi,
    onSuccess: async (address) => {
      await fetchCurrentClientAndSetOnboardingSteps();
      onboardingAddressInfoForm.reset({
        state: address?.state,
        city: address?.city,
        postcode: address?.postcode,
        address: address?.address,
      });
      showToast({
        type: 'success',
        title: t('address_information_updated_title'),
        description: t('address_information_updated_desc'),
      });
    },
    onError: (e: AxiosError<IGenericError>) => {
      onboardingAddressInfoForm.reset();
      showToast({
        type: 'error',
        title: t('address_information_updated_error_title'),
        description: t(e?.response?.data.context?.translationKey ?? '', e?.response?.data?.detail ?? e.message),
      });
    },
  });

  const onboardingBasicInfoOnSubmit = useCallback(
    (data: OnboardingSchemaBasicInformationType, e?: BaseSyntheticEvent) => {
      e?.preventDefault();
      onBoardingBasicInfoMutation.mutate(data);
    },
    [onBoardingBasicInfoMutation],
  );

  const onboardingAddressInfoOnSubmit = useCallback(
    (data: OnboardingSchemaAddressInformationType, e?: BaseSyntheticEvent) => {
      e?.preventDefault();
      onBoardingAddressInfoMutation.mutate(data);
    },
    [onBoardingAddressInfoMutation],
  );

  useEffect(() => {
    const handleFocus = async () => {
      if (popupWasOpen.current) {
        await queryClient.invalidateQueries({ queryKey: ['current-client'] });
        popupWasOpen.current = false;
      }
    };
    window.addEventListener('focus', handleFocus);

    return () => window.removeEventListener('focus', handleFocus);
  }, [queryClient]);

  const sumSubUrl = client?.client_details?.company?.shortName?.toString();
  const handleClickVerify = useCallback(
    async (e?: BaseSyntheticEvent) => {
      e?.preventDefault();

      if (!sumSubUrl) {
        await queryClient.invalidateQueries({ queryKey: ['current-client'] });

        return;
      }

      const win = window.open(sumSubUrl, '_blank', 'width=600,height=800');

      if (win) popupWasOpen.current = true;
    },
    [queryClient, sumSubUrl],
  );

  const currentIndex = Math.max(0, availableSteps.indexOf(step as OnboardingSteps));
  const verificationStep =
    step === OnboardingSteps.MISSING_VERIFICATION && availableSteps.includes(OnboardingSteps.MISSING_VERIFICATION);
  const basicInformationStep =
    step === OnboardingSteps.MISSING_BASIC_INFORMATION &&
    availableSteps.includes(OnboardingSteps.MISSING_BASIC_INFORMATION);
  const addressInformationStep =
    step === OnboardingSteps.MISSING_ADDRESS_INFORMATION &&
    availableSteps.includes(OnboardingSteps.MISSING_ADDRESS_INFORMATION);
  const isContentShouldBeShown = verificationStep || basicInformationStep || addressInformationStep;

  const handleNext = useCallback(() => {
    const idx = availableSteps.indexOf(step as OnboardingSteps);

    if (idx !== -1 && idx < availableSteps.length - 1) {
      setStep(availableSteps[idx + 1]);
    }
  }, [step, setStep, availableSteps]);

  const handleBack = useCallback(() => {
    const idx = availableSteps.indexOf(step as OnboardingSteps);

    if (idx > 0) {
      setStep(availableSteps[idx - 1]);
    }
  }, [step, setStep, availableSteps]);

  const handleClose = useCallback(
    (open: boolean) => {
      queryClient.invalidateQueries({ queryKey: ['current-client'] });

      return open;
    },
    [queryClient],
  );

  useEffect(() => {
    if (!step) {
      handleClose(false);
    }
  }, [step, handleClose]);

  return (
    <DsModal
      size="lg"
      isDismissable={false}
      isOpen={onboardingModalOpen && isContentShouldBeShown}
      onOpenChange={(open) => setOnboardingModalOpen(() => handleClose(open))}
    >
      {(close) => (
        <div className="flex max-h-[inherit] flex-col gap-4 rounded-2xl bg-backgroundRaised p-5 shadow-lg ring-1 ring-borderSurface">
          <div className="relative flex flex-col items-center gap-2 text-center">
            <DsIconButton
              name="x"
              size="sm"
              aria-label={t('close')}
              onPress={close}
              className="absolute -end-2 top-0"
            />
            <div className="flex items-center justify-center">
              {Array.from({ length: availableSteps.length }).map((_, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className={cn(
                      'w-4 h-4 rounded-full transition-all duration-300 ease-in-out',
                      index <= currentIndex ? 'bg-brandPrimary' : 'bg-brandPrimarySubtle',
                    )}
                  />
                  {index < availableSteps.length - 1 && (
                    <div
                      className={cn('w-8 h-0.5', index < currentIndex ? 'bg-brandPrimary' : 'bg-brandPrimarySubtle')}
                    />
                  )}
                </div>
              ))}
            </div>
            <DsHeading slot="title" level={2} className="pt-4 text-lg font-medium">
              {t('know_you_better')}
            </DsHeading>
            <p className="text-base text-textSecondary">
              {isRTL ? (
                <>
                  {verificationStep && <>{t('sum_sub_verification')} </>}
                  {basicInformationStep && <>{t('basic_info')} </>}
                  {addressInformationStep && <>{t('address_info')} </>}
                  &nbsp;- {currentIndex + 1} {t('step')}
                </>
              ) : (
                <>
                  {t('step')} {currentIndex + 1} -&nbsp;
                  {verificationStep && <>{t('sum_sub_verification')}</>}
                  {basicInformationStep && <>{t('basic_info')}</>}
                  {addressInformationStep && <>{t('address_info')}</>}
                </>
              )}
            </p>
          </div>

          <div className="-m-1 min-h-0 overflow-y-auto p-1">
            {verificationStep && (
              <OnboardingVerify
                handleClickVerify={handleClickVerify}
                sumSubUrl={sumSubUrl}
                isFetching={clientQuery.isFetching}
              />
            )}
            {basicInformationStep && (
              <OnboardingBasicInfoUpdate
                onboardingBasicInfoForm={onboardingBasicInfoForm}
                onboardingBasicInfoOnSubmit={onboardingBasicInfoOnSubmit}
                isPending={onBoardingBasicInfoMutation.isPending}
              />
            )}
            {addressInformationStep && (
              <OnboardingAddressInfoUpdate
                onboardingAddressInfoForm={onboardingAddressInfoForm}
                onboardingAddressInfoOnSubmit={onboardingAddressInfoOnSubmit}
                isPending={onBoardingAddressInfoMutation.isPending}
              />
            )}
          </div>

          <div className="flex justify-between pt-5">
            <DsButton onPress={handleBack} isDisabled={currentIndex <= 0}>
              {t('back')}
            </DsButton>
            <DsButton onPress={handleNext} isDisabled={currentIndex >= availableSteps.length - 1}>
              {t('next')}
            </DsButton>
          </div>
        </div>
      )}
    </DsModal>
  );
});

export default OnboardingModal;
